const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const WISHLIST_HERO_READY_EVENT =
  'wishlist-hero-wishlist-sdk-ready';

class VariantListModal extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.dialog = null;
    this.openButton = null;
    this.wishlistInitializationPromise = null;
  }

  connectedCallback() {
    /*
     * セクション再描画時に古いイベントを解除
     */
    this.abortController?.abort();
    this.abortController = new AbortController();

    const { signal } = this.abortController;

    this.dialog = this.querySelector(
      '[data-variant-modal-dialog]'
    );

    this.openButton = this.querySelector(
      '[data-variant-modal-open]'
    );

    if (!(this.dialog instanceof HTMLDialogElement)) {
      console.warn(
        '[variant-list-modal] data-variant-modal-dialogが見つかりません。'
      );

      return;
    }

    if (!(this.openButton instanceof HTMLButtonElement)) {
      console.warn(
        '[variant-list-modal] data-variant-modal-openが見つかりません。'
      );

      return;
    }

    /*
     * モーダル内のクリック処理
     */
    this.addEventListener('click', this.handleClick, {
      signal,
    });

    /*
     * モーダルが閉じた時の処理
     */
    this.dialog.addEventListener(
      'close',
      this.handleDialogClose,
      {
        signal,
      }
    );

    /*
     * Horizonの商品追加イベント
     */
    this.addEventListener(
      CART_LINES_UPDATE_EVENT,
      this.handleCartLinesUpdate,
      {
        signal,
      }
    );

    /*
     * Wishlist HeroのSDK準備完了イベント
     */
    document.addEventListener(
      WISHLIST_HERO_READY_EVENT,
      this.handleWishlistHeroReady,
      {
        signal,
      }
    );

    /*
     * SDKがすでに読み込まれている場合にも対応
     */
    this.initializeWishlistHero();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.wishlistInitializationPromise = null;
  }

  /**
   * モーダル内クリック
   */
  handleClick = async (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    /*
     * AMP - Back in Stock
     */
    const backInStockButton = event.target.closest(
      [
        '.BIS_trigger[data-variant-id]',
        '#BIS_trigger[data-variant-id]',
      ].join(', ')
    );

    if (
      backInStockButton instanceof HTMLElement &&
      this.contains(backInStockButton)
    ) {
      /*
       * AMP側の委譲クリックと重複しないように、
       * このクリックイベントはここで処理します。
       */
      event.preventDefault();
      event.stopPropagation();

      const variantId = Number(
        backInStockButton.dataset.variantId
      );

      if (!Number.isFinite(variantId)) {
        console.error(
          '[variant-list-modal] 再入荷リクエストのバリエーションIDが不正です。'
        );

        return;
      }

      this.setBackInStockButtonLoading(
        backInStockButton,
        true
      );

      /*
       * AMPのモーダルを開く前に
       * バリエーションモーダルを閉じます。
       */
      this.closeDialog();

      try {
        /*
         * dialogがトップレイヤーから外れるのを待ちます。
         */
        await this.waitForNextFrame();

        /*
         * AMPのAPIを待ってポップアップを開きます。
         */
        await this.openBackInStockPopup(variantId);
      } catch (error) {
        console.error(
          '[variant-list-modal] AMP - Back in Stockを開けませんでした。',
          error
        );
      } finally {
        this.setBackInStockButtonLoading(
          backInStockButton,
          false
        );
      }

      return;
    }

    /*
     * Wishlist Heroのお気に入りボタン
     */
    const wishlistButton = event.target.closest(
      [
        '[data-wishlist-hero-button]',
        '.wishlist-hero-custom-button',
      ].join(', ')
    );

    /*
     * 非ログイン時は、Wishlist Hero側の処理を継続したまま
     * バリエーションモーダルだけ閉じる
     */
    if (
      wishlistButton &&
      this.contains(wishlistButton) &&
      !this.isCustomerLoggedIn()
    ) {
      this.closeDialog();

      /*
       * preventDefaultやstopPropagationは使用しない
       * Wishlist Heroのログイン誘導処理をそのまま動作させる
       */
      return;
    }

    /*
     * モーダルを開く
     */
    const openButton = event.target.closest(
      '[data-variant-modal-open]'
    );

    if (openButton && this.contains(openButton)) {
      event.preventDefault();
      this.openDialog();

      return;
    }

    /*
     * モーダルを閉じる
     */
    const closeButton = event.target.closest(
      '[data-variant-modal-close]'
    );

    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.closeDialog();

      return;
    }

    /*
     * モーダルの背景部分をクリックした時に閉じる
     */
    if (event.target === this.dialog) {
      this.closeDialog();
    }
  };

  /**
   * 顧客のログイン状態を取得
   */
  isCustomerLoggedIn() {
    return this.dataset.customerLoggedIn === 'true';
  }

  /**
   * モーダルを開く
   */
  openDialog() {
    if (!(this.dialog instanceof HTMLDialogElement)) {
      return;
    }

    if (this.dialog.open) {
      return;
    }

    try {
      this.dialog.showModal();
    } catch (error) {
      /*
       * showModalが利用できない環境向けのフォールバック
       */
      this.dialog.setAttribute('open', '');

      console.warn(
        '[variant-list-modal] モーダルを開けませんでした。',
        error
      );
    }

    this.openButton?.setAttribute(
      'aria-expanded',
      'true'
    );

    /*
     * モーダルを開いた時にもWishlist Heroを再確認
     */
    this.initializeWishlistHero();
  }

  /**
   * モーダルを閉じる
   */
  closeDialog() {
    if (!(this.dialog instanceof HTMLDialogElement)) {
      return;
    }

    if (!this.dialog.open) {
      return;
    }

    try {
      this.dialog.close();
    } catch (error) {
      this.dialog.removeAttribute('open');

      console.warn(
        '[variant-list-modal] モーダルを閉じられませんでした。',
        error
      );
    }

    this.openButton?.setAttribute(
      'aria-expanded',
      'false'
    );
  }

  /**
   * モーダルが閉じた時
   */
  handleDialogClose = () => {
    this.openButton?.setAttribute(
      'aria-expanded',
      'false'
    );
  };

  /**
   * AMP - Back in Stockのポップアップを開く
   */
  async openBackInStockPopup(variantId) {
    const bis = await this.waitForBackInStockApi();

    /*
     * AMPのJavaScript APIを直接実行します。
     */
    if (
      bis?.popup &&
      typeof bis.popup.show === 'function'
    ) {
      await Promise.resolve(
        bis.popup.show({
          variantId,
        })
      );

      return;
    }

    /*
     * APIを取得できない場合は、
     * 商品詳細に配置されたAMPアプリブロックへ
     * クリックを転送します。
     */
    const appBlockButton = document.querySelector(
      '#BIS_trigger[data-bis-app-block]'
    );

    if (!(appBlockButton instanceof HTMLElement)) {
      throw new Error(
        'AMP - Back in StockのAPIとアプリブロックが見つかりません。'
      );
    }

    appBlockButton.dataset.variantId =
      String(variantId);

    appBlockButton.click();
  }

  /**
   * AMP - Back in Stock APIの読み込みを待つ
   */
  waitForBackInStockApi(
    timeout = 5000,
    interval = 50
  ) {
    return new Promise((resolve) => {
      const startedAt = Date.now();

      const checkApi = () => {
        const bis = window.BIS;

        if (
          bis?.popup &&
          typeof bis.popup.show === 'function'
        ) {
          resolve(bis);

          return;
        }

        if (Date.now() - startedAt >= timeout) {
          resolve(null);

          return;
        }

        window.setTimeout(checkApi, interval);
      };

      checkApi();
    });
  }

  /**
   * モーダルが閉じた後の描画を待つ
   */
  waitForNextFrame() {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    });
  }

  /**
   * AMP再入荷ボタンのローディング状態
   */
  setBackInStockButtonLoading(
    button,
    isLoading
  ) {
    if (!(button instanceof HTMLElement)) {
      return;
    }

    if (isLoading) {
      button.dataset.loading = 'true';
      button.setAttribute('aria-busy', 'true');

      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      }

      return;
    }

    delete button.dataset.loading;
    button.removeAttribute('aria-busy');

    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
    }
  }

  /**
   * 商品がカートへ追加された時
   */
  handleCartLinesUpdate = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    /*
     * このモーダル内のフォームから発生したイベントのみ処理
     */
    if (!this.contains(event.target)) {
      return;
    }

    /*
     * 商品追加以外のカート更新は対象外
     */
    if (event.action !== 'add') {
      return;
    }

    const productForm = event.target.closest(
      'product-form-component'
    );

    const addButton = productForm?.querySelector(
      '[data-variant-modal-add]'
    );

    this.setAddButtonLoading(addButton, true);

    const updatePromise = event.promise;

    /*
     * Promiseが存在しない場合は、
     * イベント発生時点でカート更新済みとして処理
     */
    if (
      !updatePromise ||
      typeof updatePromise.then !== 'function'
    ) {
      this.closeDialog();
      this.openCartDrawer();
      this.setAddButtonLoading(addButton, false);

      return;
    }

    updatePromise
      .then((result) => {
        const detail = result?.detail;

        /*
         * Horizon側でエラーが返された場合
         */
        if (detail?.didError) {
          return;
        }

        /*
         * 新しい標準カートアクション側のエラーにも対応
         */
        if (
          Array.isArray(result?.userErrors) &&
          result.userErrors.length > 0
        ) {
          return;
        }

        this.closeDialog();

        /*
         * モーダルが閉じた後にカートドロワーを表示
         */
        window.requestAnimationFrame(() => {
          this.openCartDrawer();
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error(
          '[variant-list-modal] カート追加処理に失敗しました。',
          error
        );
      })
      .finally(() => {
        this.setAddButtonLoading(
          addButton,
          false
        );
      });
  };

  /**
   * ADD TO BAGボタンのローディング状態
   */
  setAddButtonLoading(button, isLoading) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    if (isLoading) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.dataset.loading = 'true';

      return;
    }

    button.removeAttribute('aria-busy');
    delete button.dataset.loading;

    if (button.dataset.available === 'true') {
      button.disabled = false;
    }
  }

  /**
   * Horizonのカートドロワーを開く
   */
  openCartDrawer() {
    const cartDrawer =
      document.querySelector('#cart-drawer') ||
      document.querySelector(
        'cart-drawer-component'
      );

    if (!cartDrawer) {
      console.warn(
        '[variant-list-modal] カートドロワーが見つかりません。'
      );

      return;
    }

    /*
     * Horizonのドロワーコンポーネント
     */
    if (typeof cartDrawer.open === 'function') {
      try {
        cartDrawer.open();

        return;
      } catch (error) {
        console.warn(
          '[variant-list-modal] カートドロワーのopen処理に失敗しました。',
          error
        );
      }
    }

    /*
     * openメソッドが使用できない場合
     */
    const cartDialog =
      cartDrawer instanceof HTMLDialogElement
        ? cartDrawer
        : cartDrawer.querySelector('dialog');

    if (
      cartDialog instanceof HTMLDialogElement &&
      !cartDialog.open
    ) {
      try {
        cartDialog.showModal();
      } catch (error) {
        cartDialog.setAttribute('open', '');

        console.warn(
          '[variant-list-modal] カートダイアログを開けませんでした。',
          error
        );
      }
    }
  }

  /**
   * Wishlist Hero SDK準備完了
   */
  handleWishlistHeroReady = () => {
    this.initializeWishlistHero();
  };

  /**
   * Wishlist Heroボタンを初期化
   */
  async initializeWishlistHero() {
    /*
     * 同時に複数回初期化されることを防止
     */
    if (this.wishlistInitializationPromise) {
      return this.wishlistInitializationPromise;
    }

    this.wishlistInitializationPromise =
      this.runWishlistHeroInitialization();

    try {
      await this.wishlistInitializationPromise;
    } finally {
      this.wishlistInitializationPromise = null;
    }
  }

  /**
   * Wishlist Hero初期化本体
   */
  async runWishlistHeroInitialization() {
    const sdk = window.WishListHero_SDK;

    if (
      !sdk ||
      typeof sdk.InitializeAddToWishListButton !==
        'function'
    ) {
      return;
    }

    const buttons = Array.from(
      this.querySelectorAll(
        [
          '[data-wishlist-hero-button]',
          ':not([data-wishlist-hero-initialized])',
          ':not([data-wishlist-hero-initializing])',
        ].join('')
      )
    );

    if (buttons.length === 0) {
      return;
    }

    let wishlistHash = '';

    /*
     * Wishlist Heroのウィッシュリストハッシュを取得
     */
    if (typeof sdk.GetWishListHash === 'function') {
      try {
        wishlistHash =
          await sdk.GetWishListHash(true);
      } catch (error) {
        console.error(
          '[variant-list-modal] Wishlist Heroのハッシュ取得に失敗しました。',
          error
        );

        return;
      }
    }

    if (!wishlistHash) {
      console.warn(
        '[variant-list-modal] Wishlist Heroのハッシュを取得できませんでした。'
      );

      return;
    }

    const initializationTasks = buttons.map(
      async (button) => {
        button.dataset.wishlistHeroInitializing =
          'true';

        try {
          const buttonClass =
            button.dataset.wishlistButtonClass || '';

          const productId = Number(
            button.dataset.wlhId
          );

          const variantId = Number(
            button.dataset.wlhVariantid
          );

          const productPrice = Number(
            button.dataset.wlhPrice
          );

          if (!buttonClass) {
            throw new Error(
              'data-wishlist-button-classが設定されていません。'
            );
          }

          if (!Number.isFinite(productId)) {
            throw new Error(
              'data-wlh-idが正しく設定されていません。'
            );
          }

          if (!Number.isFinite(variantId)) {
            throw new Error(
              'data-wlh-variantidが正しく設定されていません。'
            );
          }

          const buttonInfo = {
            ButtonClassElement: buttonClass,
            ProductId: productId,
            ProductLink:
              button.dataset.wlhLink || '',
            ProductVariantId: variantId,
            ProductPrice: Number.isFinite(
              productPrice
            )
              ? productPrice
              : 0,
            ProductTitle:
              button.dataset.wlhName || '',
            ProductImage:
              this.normalizeWishlistImage(
                button.dataset.wlhImage || ''
              ),
            ButtonMode: 'icon_only',
            WishlistHash: wishlistHash,
          };

          await Promise.resolve(
            sdk.InitializeAddToWishListButton(
              buttonInfo
            )
          );

          button.dataset.wishlistHeroInitialized =
            'true';
        } catch (error) {
          delete button.dataset
            .wishlistHeroInitialized;

          console.error(
            '[variant-list-modal] Wishlist Heroボタンの初期化に失敗しました。',
            error
          );
        } finally {
          delete button.dataset
            .wishlistHeroInitializing;
        }
      }
    );

    await Promise.allSettled(
      initializationTasks
    );
  }

  /**
   * Wishlist Hero用画像URLへ変換
   */
  normalizeWishlistImage(imageUrl) {
    if (!imageUrl) {
      return '';
    }

    /*
     * https://cdn...から//cdn...へ変換
     */
    return imageUrl.replace(/^https?:/, '');
  }
}

if (!customElements.get('variant-list-modal')) {
  customElements.define(
    'variant-list-modal',
    VariantListModal
  );
}